import { useEffect, useRef, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import loaderIris from "../../../lotties/orange-loader.json";
import GrayLoader from "../../../lotties/gray-loader.json";
import { useRankingFetch } from "../../../hooks/fetch/useRankingFetch";
import useConfig from "../../../hooks/config/useConfig";
import useAlert from "../../../hooks/alert/useAlert";
import { AlertVariants } from "../../../context/Alert/AlertContext";
import {
    ProjectRankingType,
    RankingBasicProjectType,
    RankingSearchUserType,
    SetRankingPositionResponse,
    UserRankingType
} from "../../../interfaces/ranking";

//El feed muestra top 3 en ambos rankings (getListRankingUsers / getListRankingProjects hacen slice(0,3))
const MAX_POSITIONS = 3
const SEARCH_DELAY = 400
const MAX_RESULTS = 8

export const RankingManager = () => {

    const { allCountries, config } = useConfig()
    const { setPositionAlert, setTypeGlobalAlert, setShowGlobalAlert, setMessageGlobalAlert } = useAlert()
    const {
        getUsersRanking,
        getProjectsRanking,
        findUsersRanking,
        setUserRankingPosition,
        removeUserRankingPosition,
        setProjectRankingPosition,
        removeProjectRankingPosition,
        getProjectsForRanking
    } = useRankingFetch()

    const [countryId, setCountryId] = useState<number>(0)

    const [usersRanking, setUsersRanking] = useState<UserRankingType[]>([])
    const [projectsRanking, setProjectsRanking] = useState<ProjectRankingType[]>([])
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [loadingProjects, setLoadingProjects] = useState(false)

    //Asignar inmobiliaria
    const [userSearch, setUserSearch] = useState('')
    const [userResults, setUserResults] = useState<RankingSearchUserType[]>([])
    const [hiddenByCountry, setHiddenByCountry] = useState(0)
    const [searchingUsers, setSearchingUsers] = useState(false)
    const [selectedUser, setSelectedUser] = useState<RankingSearchUserType>()
    const [userPosition, setUserPosition] = useState(1)
    const [savingUser, setSavingUser] = useState(false)

    //Asignar proyecto
    const [allProjects, setAllProjects] = useState<RankingBasicProjectType[]>([])
    const [projectSearch, setProjectSearch] = useState('')
    const [selectedProject, setSelectedProject] = useState<RankingBasicProjectType>()
    const [projectPosition, setProjectPosition] = useState(1)
    const [savingProject, setSavingProject] = useState(false)

    const countries = [...allCountries].sort((a, b) => a.name.localeCompare(b.name))
    const selectedCountry = allCountries.find(c => c.id == countryId)

    useEffect(() => {
        if (!countryId && config?.country?.id) setCountryId(config.country.id)
    }, [config])

    useEffect(() => {
        if (countryId) {
            loadUsersRanking(countryId)
            loadProjectsRanking(countryId)
            clearUserForm()
            clearProjectForm()
        }
    }, [countryId])

    useEffect(() => {
        getProjectsForRanking().then(res => {
            if (res.success) setAllProjects(res.data)
            else showAlertMessage('danger', 'Ocurrió un error al listar los proyectos')
        }).catch(err => console.error(err))
    }, [])

    const loadUsersRanking = (country_id: number): Promise<UserRankingType[]> => {
        setLoadingUsers(true)
        return getUsersRanking(country_id).then(res => {
            setLoadingUsers(false)
            const list = res.success ? res.data : []
            setUsersRanking(list)
            return list
        }).catch(err => {
            setLoadingUsers(false)
            setUsersRanking([])
            console.error(err)
            return []
        })
    }

    const loadProjectsRanking = (country_id: number): Promise<ProjectRankingType[]> => {
        setLoadingProjects(true)
        return getProjectsRanking(country_id).then(res => {
            setLoadingProjects(false)
            const list = res.success ? res.data : []
            setProjectsRanking(list)
            return list
        }).catch(err => {
            setLoadingProjects(false)
            setProjectsRanking([])
            console.error(err)
            return []
        })
    }

    const clearUserForm = () => {
        setUserSearch('')
        setUserResults([])
        setHiddenByCountry(0)
        setSelectedUser(undefined)
        setUserPosition(1)
    }

    const clearProjectForm = () => {
        setProjectSearch('')
        setSelectedProject(undefined)
        setProjectPosition(1)
    }

    const showAlertMessage = (type: AlertVariants, message: string) => {
        setPositionAlert('bottom')
        setTypeGlobalAlert(type)
        setMessageGlobalAlert(message)
        setShowGlobalAlert(true)
        setTimeout(() => {
            setShowGlobalAlert(false)
            setTypeGlobalAlert('success')
            setMessageGlobalAlert('')
            setPositionAlert('top')
        }, 4000);
    }

    //set-project-position devuelve en message un string de debug ("Action up other {...}"), no mostrarlo
    const cleanApiMessage = (message?: string) => {
        if (!message || message.startsWith('Action')) return ''
        return message
    }

    const handleResult = (res: SetRankingPositionResponse, okMessage: string, onOk: () => void) => {
        const message = cleanApiMessage(res?.message)
        if (res?.success) {
            showAlertMessage('success', okMessage)
            onOk()
        } else {
            showAlertMessage('warning', message ? message : 'No se pudo aplicar el cambio')
        }
    }

    //Solo se puede asignar hasta una posición más que el largo actual del ranking:
    //el backend necesita a quién desplazar para calcular los puntos
    const maxPosition = (rankingLength: number) => Math.min(MAX_POSITIONS, rankingLength + 1)

    const positionOptions = (rankingLength: number) => {
        const max = maxPosition(rankingLength)
        return Array.from({ length: max }, (_, i) => i + 1)
    }

    const userTimer = useRef<any>(0)
    const searchUsers = (term: string) => {
        clearTimeout(userTimer.current)
        setUserSearch(term)
        if (term.trim().length < 2) {
            setUserResults([])
            setHiddenByCountry(0)
            return
        }
        userTimer.current = setTimeout(() => {
            setSearchingUsers(true)
            findUsersRanking(term.trim()).then(res => {
                setSearchingUsers(false)
                if (res.success && res.data) {
                    const merged = [
                        ...(res.data.by_real_estate || []),
                        ...(res.data.by_name || []),
                        ...(res.data.by_email || [])
                    ]
                    const unique = merged.filter((u, i, arr) => arr.findIndex(x => x.id == u.id) == i)
                    //find-user no filtra por país: el ranking es por país, así que dejamos solo los del país elegido
                    const ofCountry = selectedCountry
                        ? unique.filter(u => u.countryID == selectedCountry.identifier)
                        : unique
                    setUserResults(ofCountry.slice(0, MAX_RESULTS))
                    setHiddenByCountry(unique.length - ofCountry.length)
                } else {
                    setUserResults([])
                    setHiddenByCountry(0)
                }
            }).catch(err => {
                setSearchingUsers(false)
                console.error(err)
            })
        }, SEARCH_DELAY)
    }

    const submitUserPosition = () => {
        if (!selectedUser) return
        const user = selectedUser
        setSavingUser(true)
        setUserRankingPosition(user.id, userPosition).then(res => {
            setSavingUser(false)
            const message = cleanApiMessage(res?.message)
            if (!res?.success) {
                showAlertMessage('warning', message ? message : 'No se pudo aplicar el cambio')
                return
            }
            clearUserForm()
            //El backend guarda los puntos aunque el usuario no entre en el listado (ej: nivel 11 / admin)
            loadUsersRanking(countryId).then(list => {
                if (list.some(u => u.id == user.id)) {
                    showAlertMessage('success', `${userLabel(user)} quedó en la posición ${userPosition}`)
                } else {
                    showAlertMessage('warning', `Se guardaron los puntos de ${userLabel(user)} pero no aparece en el ranking (los usuarios nivel 11 / admin no se listan)`)
                }
            })
        }).catch(err => {
            setSavingUser(false)
            console.error(err)
            showAlertMessage('danger', 'Ocurrió un error')
        })
    }

    const removeUser = (user: UserRankingType) => {
        removeUserRankingPosition(user.id).then(res => {
            handleResult(res, 'Posición manual quitada', () => loadUsersRanking(countryId))
        }).catch(err => {
            console.error(err)
            showAlertMessage('danger', 'Ocurrió un error')
        })
    }

    const submitProjectPosition = () => {
        if (!selectedProject) return
        const project = selectedProject
        setSavingProject(true)
        setProjectRankingPosition(project.identifier, projectPosition).then(res => {
            setSavingProject(false)
            const message = cleanApiMessage(res?.message)
            if (!res?.success) {
                showAlertMessage('warning', message ? message : 'No se pudo aplicar el cambio')
                return
            }
            clearProjectForm()
            //El proyecto entra en el ranking de SU país: si no era del país elegido, no va a aparecer acá
            loadProjectsRanking(countryId).then(list => {
                if (list.some(p => p.identifier == project.identifier)) {
                    showAlertMessage('success', `${project.title} quedó en la posición ${projectPosition}`)
                } else {
                    showAlertMessage('warning', `Se guardaron los puntos de ${project.title} pero no aparece en el ranking de ${selectedCountry?.name}: revisá que el proyecto sea de ese país`)
                }
            })
        }).catch(err => {
            setSavingProject(false)
            console.error(err)
            showAlertMessage('danger', 'Ocurrió un error')
        })
    }

    const removeProject = (project: ProjectRankingType) => {
        removeProjectRankingPosition(project.identifier).then(res => {
            handleResult(res, 'Posición manual quitada', () => loadProjectsRanking(countryId))
        }).catch(err => {
            console.error(err)
            showAlertMessage('danger', 'Ocurrió un error')
        })
    }

    const userLabel = (user: RankingSearchUserType | UserRankingType) => user.real_estate ? user.real_estate : user.name

    const projectMatches = projectSearch.trim().length < 2
        ? []
        : allProjects
            .filter(p => p.title?.toLowerCase().includes(projectSearch.trim().toLowerCase()))
            .slice(0, MAX_RESULTS)

    const isManual = (item: { points: number, manual_points?: number }) => !!item.manual_points && item.manual_points > 0

    const loader = <div style={{ height: 160 }} className="w-100 d-flex align-items-center justify-content-center">
        <Lottie animationData={loaderIris} style={{ height: 70 }} loop />
    </div>

    return (
        <div className="p-4 p-md-5 admin-ranking">
            <div className="mb-4 py-2 px-2 px-md-4 d-flex justify-content-between align-items-center flex-wrap position-relative">
                <Link to="/admin" className="fs-2 go-back" title="Volver">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3 className="mb-0">RANKING</h3>
                <Form.Group className="d-flex align-items-center">
                    <Form.Label className="mb-0 me-2 text-nowrap">País:</Form.Label>
                    <Form.Select
                        value={countryId}
                        onChange={(ev) => setCountryId(parseInt(ev.target.value))}
                        style={{ minWidth: 200 }}
                    >
                        <option value={0} disabled>Seleccionar país</option>
                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Form.Select>
                </Form.Group>
            </div>

            <p className="text-muted px-2 px-md-4">
                Estos son los dos rankings que se ven en el feed. Se ordenan por puntos: cada venta del mes anterior
                suma 200 puntos y se recalcula el día 1 de cada mes. Al asignar una posición acá se fija un puntaje
                manual que le gana al automático hasta que lo quites.
            </p>

            <div className="row px-2 px-md-4">

                {/* ---------- TOP RANKING (inmobiliarias) ---------- */}
                <div className="col-12 col-xl-6 mb-5">
                    <h5 className="mb-3"><i className="bi bi-trophy-fill text-green me-2"></i>Top Ranking (inmobiliarias)</h5>

                    {loadingUsers ? loader :
                        usersRanking.length > 0 ?
                            <Table bordered hover responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: 60 }}>#</th>
                                        <th>Inmobiliaria</th>
                                        <th style={{ width: 90 }}>Nivel</th>
                                        <th style={{ width: 120 }}>Puntos</th>
                                        <th style={{ width: 60 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usersRanking.map((user, index) => (
                                        <tr key={user.id}>
                                            <td className="fw-bold">{index + 1}</td>
                                            <td>
                                                <p className="mb-0">{userLabel(user)}</p>
                                                <small className="text-muted">{user.email}</small>
                                            </td>
                                            <td>{user.level}</td>
                                            <td>
                                                {user.points}
                                                {isManual(user) && <span className="badge bg-warning text-dark ms-2">manual</span>}
                                            </td>
                                            <td>
                                                {isManual(user) &&
                                                    <button
                                                        className="btn btn-outline-primary border-0"
                                                        title="Quitar posición manual"
                                                        onClick={() => removeUser(user)}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            : <p className="text-muted">No hay inmobiliarias en el ranking de este país.</p>
                    }

                    <div className="border rounded p-3 mt-3">
                        <p className="fw-500 mb-3">Asignar posición</p>
                        <Form.Group className="mb-2">
                            <Form.Control
                                placeholder="Buscar por inmobiliaria, nombre o email..."
                                value={userSearch}
                                onChange={(ev) => searchUsers(ev.target.value)}
                            />
                        </Form.Group>

                        {searchingUsers && <small className="text-muted">Buscando...</small>}

                        {!selectedUser && userResults.length > 0 &&
                            <div className="list-group mb-2">
                                {userResults.map(user => (
                                    <button
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        key={user.id}
                                        onClick={() => setSelectedUser(user)}
                                    >
                                        <span className="fw-500">{userLabel(user)}</span>
                                        <small className="text-muted ms-2">{user.email}</small>
                                    </button>
                                ))}
                            </div>
                        }

                        {!selectedUser && userSearch.trim().length > 1 && !searchingUsers && userResults.length == 0 &&
                            <small className="text-muted d-block mb-2">
                                Sin resultados en {selectedCountry?.name}
                                {hiddenByCountry > 0 && ` (${hiddenByCountry} coincidencia/s en otros países)`}
                            </small>
                        }

                        {selectedUser &&
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <span className="badge bg-light text-dark border p-2">
                                    {userLabel(selectedUser)}
                                    <i className="bi bi-x ms-2" role="button" onClick={() => setSelectedUser(undefined)}></i>
                                </span>
                                <Form.Select
                                    value={userPosition}
                                    onChange={(ev) => setUserPosition(parseInt(ev.target.value))}
                                    style={{ width: 140 }}
                                >
                                    {positionOptions(usersRanking.length).map(p => (
                                        <option key={p} value={p}>Posición {p}</option>
                                    ))}
                                </Form.Select>
                                <Button
                                    variant="primary"
                                    className="position-relative"
                                    disabled={savingUser}
                                    onClick={submitUserPosition}
                                >
                                    ASIGNAR
                                    {savingUser && <Lottie className="position-absolute loader-btn" animationData={GrayLoader} style={{ height: 28, right: 0, top: 4 }} loop />}
                                </Button>
                            </div>
                        }
                        <small className="text-muted d-block mt-3">
                            Solo se puede asignar hasta la posición {maxPosition(usersRanking.length)} (no se puede dejar un hueco en el medio).
                            Los usuarios nivel 11 / admin no se muestran en el ranking.
                        </small>
                    </div>
                </div>

                {/* ---------- LO MÁS VENDIDO (proyectos) ---------- */}
                <div className="col-12 col-xl-6 mb-5">
                    <h5 className="mb-3"><i className="bi bi-building text-green me-2"></i>Lo más vendido (proyectos)</h5>

                    {loadingProjects ? loader :
                        projectsRanking.length > 0 ?
                            <Table bordered hover responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: 60 }}>#</th>
                                        <th>Proyecto</th>
                                        <th style={{ width: 120 }}>Puntos</th>
                                        <th style={{ width: 60 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projectsRanking.map((project, index) => (
                                        <tr key={project.id}>
                                            <td className="fw-bold">{index + 1}</td>
                                            <td>
                                                <p className="mb-0">{project.title}</p>
                                                <small className="text-muted">ID {project.identifier}</small>
                                            </td>
                                            <td>
                                                {project.points}
                                                {isManual(project) && <span className="badge bg-warning text-dark ms-2">manual</span>}
                                            </td>
                                            <td>
                                                {isManual(project) &&
                                                    <button
                                                        className="btn btn-outline-primary border-0"
                                                        title="Quitar posición manual"
                                                        onClick={() => removeProject(project)}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            : <p className="text-muted">No hay proyectos en el ranking de este país.</p>
                    }

                    <div className="border rounded p-3 mt-3">
                        <p className="fw-500 mb-3">Asignar posición</p>
                        <Form.Group className="mb-2">
                            <Form.Control
                                placeholder="Buscar proyecto por nombre..."
                                value={projectSearch}
                                onChange={(ev) => { setProjectSearch(ev.target.value); setSelectedProject(undefined) }}
                            />
                        </Form.Group>

                        {!selectedProject && projectMatches.length > 0 &&
                            <div className="list-group mb-2">
                                {projectMatches.map(project => (
                                    <button
                                        type="button"
                                        className="list-group-item list-group-item-action"
                                        key={project.id}
                                        onClick={() => setSelectedProject(project)}
                                    >
                                        <span className="fw-500">{project.title}</span>
                                        <small className="text-muted ms-2">ID {project.identifier}</small>
                                    </button>
                                ))}
                            </div>
                        }

                        {!selectedProject && projectSearch.trim().length > 1 && projectMatches.length == 0 &&
                            <small className="text-muted d-block mb-2">Sin resultados</small>
                        }

                        {selectedProject &&
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <span className="badge bg-light text-dark border p-2">
                                    {selectedProject.title}
                                    <i className="bi bi-x ms-2" role="button" onClick={() => setSelectedProject(undefined)}></i>
                                </span>
                                <Form.Select
                                    value={projectPosition}
                                    onChange={(ev) => setProjectPosition(parseInt(ev.target.value))}
                                    style={{ width: 140 }}
                                >
                                    {positionOptions(projectsRanking.length).map(p => (
                                        <option key={p} value={p}>Posición {p}</option>
                                    ))}
                                </Form.Select>
                                <Button
                                    variant="primary"
                                    className="position-relative"
                                    disabled={savingProject}
                                    onClick={submitProjectPosition}
                                >
                                    ASIGNAR
                                    {savingProject && <Lottie className="position-absolute loader-btn" animationData={GrayLoader} style={{ height: 28, right: 0, top: 4 }} loop />}
                                </Button>
                            </div>
                        }
                        <small className="text-muted d-block mt-3">
                            Solo se puede asignar hasta la posición {maxPosition(projectsRanking.length)}.
                            El proyecto entra en el ranking de su propio país: si no aparece arriba, revisá que sea de {selectedCountry?.name}.
                        </small>
                    </div>
                </div>
            </div>
        </div>
    )
}
